import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Theme, Typography, useMediaQuery } from "@mui/material";
import { Investment } from "@rentablo/api/apiv1";
import { InvestmentChart, PHONE_CHART_HEIGHT } from "@components";
import { GainLossPercents } from "@rentablo/components";
import {
  getValueByInvestmentAndBookingType,
  useMandatorVisibility,
} from "@helpers";
import { STOCK_INDEXES } from "@constants";
import { useCurrencyDisplay } from "@providers/CurrencyDisplayProvider";
import {
  MobileRangeControl,
  useChartControls,
} from "@components/Chart/ChartControls";
import { useChartData } from "../hooks/useChartData";
import { useInvestmentCurrencyView } from "../hooks/useInvestmentCurrencyView";
import { RecommendedItems } from "../types";
import { getDisplayedQuoteValue } from "./InvestmentTable/getDisplayedQuoteValue";
import { PerformanceOverview } from "./PerformanceOverview";

const InvestmentChartStyled = styled(InvestmentChart)`
  min-height: 546px;

  ${({ theme }) => theme.breakpoints.down("lg")} {
    min-height: 427px;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    min-height: auto;
    box-shadow: none;
  }
`;

const InvestmentChartTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing(5)};
  ${({ theme }) => theme.breakpoints.down("md")} {
    margin-bottom: ${({ theme }) => theme.spacing(0)};
  }
`;

const InvestmentChartTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface Props {
  investmentId: number;
  investment?: Investment;
  isInvestmentLoading: boolean;
  isInvestmentPending: boolean;
  recommendedItems: RecommendedItems;
}

export const InvestmentPageChart = ({
  investmentId,
  investment,
  isInvestmentLoading,
  isInvestmentPending,
  recommendedItems,
}: Props) => {
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isPerformanceOverviewVisible = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });
  const {
    rangeProps,
    comparedTicker,
    setComparedTicker,
    availableDateRanges,
    availableCompactDateRanges,
  } = useChartControls();
  const { selectedCurrencyView, getDisplayCurrency } = useCurrencyDisplay();
  const isOriginal = selectedCurrencyView === "original";
  const displayCurrency = getDisplayCurrency(investment?.currency);

  const {
    netGain,
    returnOnInvestment,
    returnOnInvestmentInPercent,
    performance,
    gainAndLoss,
  } = useInvestmentCurrencyView({
    investmentId,
    investment,
    displayCurrency,
    isOriginal,
  });

  const {
    data: chartData = [],
    hasRecommenderData,
    isFetching,
  } = useChartData(
    {
      investmentId,
      currentInterval: rangeProps.intervalType,
      tickerSymbol: investment?.tickerSymbol,
      tickerSymbolWithCurrency: investment?.tickerSymbolWithCurrency,
      quoteProvider: investment?.quoteProvider,
      startDate: rangeProps.range.startDate,
      endDate: rangeProps.range.endDate,
      comparedTickerSymbol: comparedTicker,
      recommendedItems,
      currency: isOriginal ? investment?.currency : displayCurrency,
    },
    { showEvents: true }
  );

  useEffect(() => {
    // comparison is not available for intraday data
    if (rangeProps.intervalType === "oneDay") {
      setComparedTicker(undefined);
    }
  }, [rangeProps.intervalType, setComparedTicker]);

  const quoteValue = getDisplayedQuoteValue(investment?.quote, isOriginal);
  const chartHeaderValue =
    getValueByInvestmentAndBookingType({
      value: quoteValue,
      investmentType: investment?.type ?? "11_stock",
      currency: displayCurrency,
      percentageRange: "0-100",
      quoteNotation: investment?.quoteNotation,
    }) ?? 0;

  const handleComparedValueChange = (value: string | null) => {
    setComparedTicker(value !== null ? value : undefined);
  };

  return (
    <InvestmentChartStyled
      showEvents
      isFetching={isFetching}
      drawLines={!isSm}
      showNavigator={!isSm}
      chartHeight={isSm ? PHONE_CHART_HEIGHT : undefined}
      rangeProps={rangeProps}
      hideRangeSelector={isSm}
      figureAction={isSm ? <MobileRangeControl /> : undefined}
      availableDateRanges={availableDateRanges}
      availableCompactDateRanges={availableCompactDateRanges}
      hasRecommenderData={hasRecommenderData}
      title={
        <InvestmentChartTitleWrapper>
          <Typography variant="body2">
            {t("investment.header.chart.subheader")}
          </Typography>
          <InvestmentChartTitle>
            <Typography variant={isSm ? "h4" : "h2"}>
              {chartHeaderValue}
            </Typography>
            <GainLossPercents
              value={investment?.quote?.changePercentAbs ?? 0}
            />
          </InvestmentChartTitle>
        </InvestmentChartTitleWrapper>
      }
      comparedTicker={comparedTicker}
      onComparedTickerChange={handleComparedValueChange}
      recommendedItems={recommendedItems}
      comparisonValues={STOCK_INDEXES}
      bottomBarComponent={
        isPerformanceOverviewVisible ? (
          <PerformanceOverview
            showPerformanceReview={!isInvestmentLoading || !isInvestmentPending}
            currency={displayCurrency}
            gainAndLoss={gainAndLoss}
            realizedGains={netGain}
            returnOnInvestment={returnOnInvestment}
            returnOnInvestmentInPercent={returnOnInvestmentInPercent}
            performance={performance}
          />
        ) : undefined
      }
      data={chartData}
    />
  );
};
